import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import { Helmet } from "react-helmet"
import parse from "html-react-parser"

import Layout from "@components/main/layout/Layout"
import AllLayouts from "../../components/main/AllLayouts"
import { HomeHero } from "@components/main/home-hero/HomeHero"
import { GermanyHero } from "@components/main/germany-hero/GermanyHero"
import { BlogPostsSummary } from "@components/main/blog-posts-summary/BlogPostsSummary"
import { SubHeader } from "@components/main/sub-header/SubHeader"
import { SimpleHeader } from "@components/main/simple-header/SimpleHeader"
import { ShopHeader } from "@components/main/shop-header/ShopHeader"
import { PaymentBanner } from "@components/main/payment-banner/PaymentBanner"
import { SupportHeader } from "@components/main/support-header/SupportHeader"
import { ChristmasHeader } from "@components/main/christmas-header/ChristmasHeader"
import { ChildHeader } from "@components/main/child-header/ChildHeader"
import { GermanyHeader } from "@components/main/germany-header/GermanyHeader"
import { SEO as GlobalSEO } from "@components/common/seo/SEO"
import RecommendedGallery from "@components/main/shopify-gallery/RecommendedGallery"

import * as s from "./Page.module.scss"

const Page = ({ pageContext, data }) => {
  const { wpPage, allWpPost } = data
  const [domLoaded, setDomLoaded] = useState(false)

  useEffect(() => {
    setDomLoaded(true) // Why this site runs into so many hyradtion issues!?
  }, [])

  const {
    template,
    acfChild,
    acfGermany,
    acfShop,
    acfHome,
    acfHub,
    acfMultiApp,
  } = wpPage
  const { acfSeo, ancestors, title, flexibleContent } = pageContext.page
  const { seoTags, seoTitle, ogImage } = acfSeo
  const Posts = allWpPost.nodes
  const layouts = flexibleContent?.content || []
  let isChristmas = template === "Christmas"

  useEffect(() => {
    const script = document.createElement("script")
    script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=QR9uPq`
    script.type = "text/javascript"
    document.body.appendChild(script)
  }, [])

  if (template?.templateName === "Multi App" && domLoaded) {
    return (
      <> 
        <Helmet>
          <script
            type="module"
            crossOrigin
            src={acfMultiApp.javascript}
          ></script>
          <link rel="stylesheet" crossOrigin href={acfMultiApp.css}></link>

          {acfMultiApp.googleScripts && parse(acfMultiApp.googleScripts)}
        </Helmet>
        <div id="app"></div>
      </>
    )
  }

  return (
    <Layout pageData={pageContext} template={template.templateName}>
      <div className={`${s.Page} ${isChristmas ? s.Page___christmas : ""}`}>
        <GlobalSEO
          data={{
            tags: seoTags,
            title: seoTitle,
            image: ogImage,
          }}
        />

        {acfHome.heroSlides && template.templateName === "Home" && (
          <HomeHero data={acfHome.heroSlides} />
        )}

        {acfGermany.topImage && template.templateName === "Germany" && (
          <GermanyHero data={acfGermany} />
        )}

        {isChristmas && <ChristmasHeader />}

        {(() => {
          switch (template.templateName) {
            case "Hub":
              return <SubHeader data={acfHub} />
            case "Simple":
              return <SimpleHeader title={title} />
            case "Support":
              return <SupportHeader title="Customer Care" />
            case "Shop":
              return (
                <ShopHeader
                  title={acfShop.headerTitle}
                  image={acfShop.headerImage}
                />
              )
            case "Child":
              return (
                <ChildHeader
                  data={acfChild}
                  subTitle={
                    ancestors &&
                    ancestors.nodes.length &&
                    ancestors.nodes[0].title
                  }
                />
              )
            case "Germany":
              return (
                <GermanyHeader
                  data={acfGermany}
                  subTitle={
                    ancestors &&
                    ancestors.nodes.length &&
                    ancestors.nodes[0].title
                  }
                />
              )
            default:
              return null
          }
        })()}

        {layouts.map((layout, index) => {
          return (
            <AllLayouts
              key={index}
              layoutData={layout}
              pageContext={pageContext}
            />
          )
        })}

        {Posts && template.templateName === "Home" && (
          <BlogPostsSummary posts={Posts} />
        )}

        {template.templateName === "Shop" && <PaymentBanner />}

        {isChristmas && (
          <RecommendedGallery collection="christmas" vGrid={true} />
        )}
      </div>
    </Layout>
  )
}

export default Page

export const query = graphql`
  query PageQuery($slug: String) {
    wpPage(slug: { eq: $slug }) {
      title
      template {
        templateName
      }

      acfMultiApp {
        javascript
        css
        googleScripts
      }

      acfHub {
        title
        blurb
        scrollText
        direction
        blocks2 {
          image {
            sourceUrl
          }
          type
          videoUrl
          videoFile {
            sourceUrl
          }
          instagramLink
          instagramHandle
          instagramProfileImage {
            sourceUrl
          }
        }
        links {
          link {
            url
            title
            target
          }
        }
      }

      acfHome {
        heroSlides {
          title
          blurb
          image {
            localFile {
              childImageSharp {
                gatsbyImageData(
                  placeholder: BLURRED
                  quality: 60
                  formats: [WEBP]
                  width: 1920
                )
              }
            }
          }
          video {
            sourceUrl
          }
          links {
            link {
              target
              title
              url
            }
          }
        }
      }

      acfShop {
        headerTitle
        headerImage {
          altText
          localFile {
            childImageSharp {
              gatsbyImageData(
                placeholder: BLURRED
                quality: 80
                formats: [WEBP]
              )
            }
          }
        }
      }

      acfChild {
        innerBlock {
          color
          content
        }
        blocks {
          image {
            sourceUrl
          }
          type
          videoUrl
          videoFile {
            sourceUrl
          }
          instagramLink
          instagramHandle
          instagramProfileImage {
            sourceUrl
          }
        }
        links {
          link {
            url
            title
            target
          }
        }
        backgroundColor
        scrollText
        blurb
        title
      }

      acfGermany {
        topImage {
          localFile {
            publicURL
          }
        }
        topImageMobile {
          localFile {
            publicURL
          }
        }
        gInnerBlock {
          color
          content
        }
        gBlocks {
          image {
            sourceUrl
          }
          type
          videoUrl
          videoFile {
            sourceUrl
          }
          instagramLink
          instagramHandle
          instagramProfileImage {
            sourceUrl
          }
        }
        gLinks {
          link {
            url
            title
            target
          }
        }
        backgroundColor
        blurb
        title
        contentImage {
          localFile {
            publicURL
          }
        }
        contentImage2 {
          localFile {
            publicURL
          }
        }
      }
    }

    allWpPost(
      sort: { date: DESC }
      limit: 3
      filter: { status: { eq: "publish" }, slug: { ne: "dummy" } }
    ) {
      nodes {
        title
        slug
        featuredImage {
          node {
            sourceUrl
            altText
            localFile {
              childImageSharp {
                gatsbyImageData(
                  placeholder: BLURRED
                  quality: 80
                  formats: [WEBP]
                )
              }
            }
          }
        }
        categories {
          nodes {
            name
            slug
          }
        }
        date(formatString: "MMM DD, YYYY")

        acfPost {
          excerpt
        }
      }
    }
  }
`
